import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import { changeTitleName, changePdfFileName, changeIndication } from "../../../redux/actions/emailAction";
import { getSelectedSheetsData, genDiuoPDF, getDiuoFile } from "../Utils";
import { Button, IconButton } from "@material-ui/core";
import { Add as AddIcon, Save as SaveIcon, PictureAsPdf as PdfIcon, Visibility as VisibilityIcon, ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, Delete as DeleteIcon } from "@material-ui/icons";
import history from "../../Routes/history";
import Header from "../../Header/Header";
import DocMenu from "../../DocMenu";
import NumDossDIUO from "../../NumDossDIUO";
import TableComponent from './TableComponent';
import GenPdf from "../../GenPdf";
import LoadingOverlay from "../../LoadingOverlay";
import { checkboxes, checkboxesContentsConst } from './Const';
import "../../../Styles/DIUO/Page7.scss";

const DiuoPage7 = () => {
  const dispatch = useDispatch();
  const dossierValue = useSelector((state) => state.devis.dossierValue);
  const index = useSelector((state) => state.diuo.index);
  const diuoValue = useSelector((state) => state.diuo.diuoValue);

  const [isLoading, setIsLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState({});
  const [savedSheetImages, setSavedSheetImages] = useState({});
  const [newSheetTitle, setNewSheetTitle] = useState('');
  const [dynamicSheets, setDynamicSheets] = useState(new Set());

  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      checkboxes.splice(38);
      if (!index || !diuoValue) {
        setSelectedSheets({});
        setSavedSheetImages({});
        setDynamicSheets(new Set());
        return;
      }

      const { selectedSheets, images } = await getSelectedSheetsData(dossierValue, index, diuoValue);

      const updatedSheets = { ...selectedSheets };
      const newDynamicSheets = new Set();

      Object.entries(selectedSheets).forEach(([sheetKey, sheetData]) => {
        const sheetIndex = parseInt(sheetKey.replace('sheet', '')) - 1;
        if (sheetIndex >= 38 && sheetData.title) {
          checkboxes.push(sheetData.title);
          checkboxesContentsConst.push({
            description: "",
            localization: "",
            natureOfInterventions: "",
            access: "",
            risks: "",
            securityMeasures: ""
          });
          newDynamicSheets.add(sheetIndex);
        }
      });

      setDynamicSheets(newDynamicSheets);

      const newSavedImages = {};

      for (const image of images) {
        const { img, name } = image;
        const buffer = new Uint8Array(img.buffer.data).buffer;
        const file = new File([buffer], name, { type: 'image/png' });

        const [, sheetNumber, imgNumber] = name.match(/sheet(\d+).*img(\d+)/);
        const sheetKey = `sheet${sheetNumber}`;

        if (!updatedSheets[sheetKey]) {
          updatedSheets[sheetKey] = {};
        }
        updatedSheets[sheetKey][`img${imgNumber}`] = file;
        newSavedImages[`${sheetKey}_img${imgNumber}`] = name;
      }

      setSavedSheetImages(newSavedImages);
      setSelectedSheets(updatedSheets);
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!dossierValue) return
    loadData()
  }, [diuoValue]);

  const handleAddNewSheet = () => {
    if (!newSheetTitle.trim()) return;

    const newIndex = checkboxes.length;

    checkboxes.push(newSheetTitle);

    checkboxesContentsConst.push({
      description: "",
      localization: "",
      natureOfInterventions: "",
      access: "",
      risks: "",
      securityMeasures: ""
    });

    setDynamicSheets(prev => new Set([...prev, newIndex]));

    handleCheckboxChange(newIndex);

    setNewSheetTitle('');
  };

  const handleDeleteSheet = (sheetIndex) => {
    // Supprimer de selectedSheets
    setSelectedSheets(prevSelected => {
      const newSelected = { ...prevSelected };
      delete newSelected[`sheet${sheetIndex + 1}`];
      return newSelected;
    });

    // Supprimer de dynamicSheets
    setDynamicSheets(prev => {
      const newSet = new Set(prev);
      newSet.delete(sheetIndex);
      return newSet;
    });

    // Supprimer des arrays globaux
    checkboxes.splice(sheetIndex, 1);
    checkboxesContentsConst.splice(sheetIndex, 1);

    // Mettre à jour les indices des sheets suivantes
    setSelectedSheets(prevSelected => {
      const newSelected = {};
      const oldKeys = Object.keys(prevSelected).sort((a, b) => {
        const numA = parseInt(a.replace('sheet', ''), 10);
        const numB = parseInt(b.replace('sheet', ''), 10);
        return numA - numB;
      });

      oldKeys.forEach(oldKey => {
        const oldIndex = parseInt(oldKey.replace('sheet', ''), 10) - 1;
        if (oldIndex > sheetIndex) {
          const newKey = `sheet${oldIndex}`;
          newSelected[newKey] = prevSelected[oldKey];
        } else if (oldIndex < sheetIndex) {
          newSelected[oldKey] = prevSelected[oldKey];
        }
      });

      return newSelected;
    });

    setDynamicSheets(prev => {
      const newSet = new Set();
      prev.forEach(index => {
        if (index > sheetIndex) {
          newSet.add(index - 1);
        } else if (index < sheetIndex) {
          newSet.add(index);
        }
      });
      return newSet;
    });
  };

  const handleCheckboxChange = (index) => {
    setSelectedSheets((prevSelected) => {
      const newSelected = { ...prevSelected };
      const key = `sheet${index + 1}`;
      if (newSelected[key]) {
        delete newSelected[key];
      } else {
        newSelected[key] = {
          title: checkboxes[index],
          description: checkboxesContentsConst[index].description,
          localization: checkboxesContentsConst[index].localization,
          natureOfInterventions: checkboxesContentsConst[index].natureOfInterventions,
          access: checkboxesContentsConst[index].access,
          risks: checkboxesContentsConst[index].risks,
          securityMeasures: checkboxesContentsConst[index].securityMeasures,
          img1: null,
          img2: null
        };
      }
      return newSelected;
    });
  };

  const handleInputChange = (key, field, value) => {
    setSelectedSheets((prevSelected) => {
      const newSelected = { ...prevSelected };
      newSelected[key][field] = value;
      return newSelected;
    });
    if (field.startsWith('img') && value !== null) {
      setSavedSheetImages(prev => {
        const next = { ...prev };
        delete next[`${key}_${field}`];
        return next;
      });
    }
  };

  const sortedKeys = Object.keys(selectedSheets).sort((a, b) => {
    const numA = parseInt(a.replace('sheet', ''), 10);
    const numB = parseInt(b.replace('sheet', ''), 10);
    return numA - numB;
  });

  const handleSaveData = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append('ndossier', dossierValue);
    formData.append('index', index);
    formData.append('diuoValue', diuoValue);

    const imagesToDelete = [];

    Object.keys(selectedSheets).forEach((sheetKey) => {
      const sheet = selectedSheets[sheetKey];

      formData.append(`${sheetKey}_title`, sheet.title);
      formData.append(`${sheetKey}_description`, sheet.description);
      formData.append(`${sheetKey}_localization`, sheet.localization);
      formData.append(`${sheetKey}_natureOfInterventions`, sheet.natureOfInterventions);
      formData.append(`${sheetKey}_access`, sheet.access);
      formData.append(`${sheetKey}_risks`, sheet.risks);
      formData.append(`${sheetKey}_securityMeasures`, sheet.securityMeasures);

      Object.keys(sheet).forEach((key) => {
        if (!key.startsWith('img')) return;
        const compositeKey = `${sheetKey}_${key}`;
        if (sheet[key] instanceof File && !savedSheetImages[compositeKey]) {
          formData.append(`${sheetKey}_${key}`, sheet[key]);
        } else if (sheet[key] === null && savedSheetImages[compositeKey]) {
          imagesToDelete.push(savedSheetImages[compositeKey]);
        }
      });
    });

    if (imagesToDelete.length > 0) {
      formData.append('imagesToDelete', JSON.stringify(imagesToDelete));
    }

    try {
      const response = await fetch('/api/diuo/saveSelectedSheets', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        console.error('Failed to upload images');
      }
    } catch (err) {
      console.error('Error saving technical lots:', err.message);
    } finally {
      await loadData();
      setIsLoading(false);
    }
  };

  const handleGenClick = useCallback(async () => {
    setDisableButton(true);
    setFile(null);
    let blob = await genDiuoPDF(diuoValue, dossierValue, index);
    if (blob) {
      setIsOpen(true);
      setFile(blob);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    setDisableButton(false);
  }, [diuoValue]);

  const handleShowPdf = useCallback(async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getDiuoFile(dossierValue, index, 'pdf');
    if (response.blob.size > 100) {
      setFile(response.blob);
      setIsOpen(true);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    setDisableButton(false);
  }, [diuoValue]);

  const handleGetPdfFile = useCallback(async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getDiuoFile(dossierValue, index, 'pdf');
    if (response.blob.type === 'application/pdf') {
      setFile(response.blob);
      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName))
    }
    setDisableButton(false);
  }, [diuoValue]);

  const handleGetWordFile = useCallback(async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getDiuoFile(dossierValue, index, 'word');
    if (response.blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName))
    }
    setDisableButton(false);
  }, [diuoValue]);

  const handleChangeTitleName = useCallback(() => {
    dispatch(changeTitleName(`DIUO`));
  }, [diuoValue]);

  const prevPage = () => {
    history.push("/diuo/page6");
  }

  const nextPage = () => {
    history.push("/diuo/page8");
  }

  return (
    <div className="diuo-page7">
      {isLoading && <LoadingOverlay />}
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
        updateIndication={() => dispatch(changeIndication(index))}
      />
      <h1 className="title">DIUO (Fiches techniques)</h1>
      <NumDossDIUO />
      <div className="container">
        <div>
          <div className="construction-elements__add-new">
            <input
              type="text"
              placeholder="&quot;Nouvelle fiche technique&quot;"
              value={newSheetTitle}
              onChange={(e) => setNewSheetTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddNewSheet();
                }
              }}
              style={{ marginTop: "25px", boxShadow: "0 0 7px #7a7a7a" }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewSheet}
            >
              Ajouter
            </Button>
          </div>
          <div className="checkbox-grid">
            {checkboxes.map((label, index) => (
              <div 
                key={index} 
                className={index % 2 === 0 ? "checkbox-item-left" : "checkbox-item-right"}
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={selectedSheets[`sheet${index + 1}`] !== undefined}
                    onChange={() => handleCheckboxChange(index)}
                  />
                  &nbsp;{label}
                </label>
                {dynamicSheets.has(index) && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSheet(index)}
                    style={{ 
                      padding: '2px',
                      color: '#f44336',
                      marginLeft: '8px',
                      minWidth: 'auto'
                    }}
                    title="Supprimer cette fiche technique"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="table-container">
          {sortedKeys.map((key, index) => (
            <TableComponent
              index={index}
              key={key}
              sheet={selectedSheets[key]}
              sheetNumber={parseInt(key.replace('sheet', ''))}
              onInputChange={(field, value) => handleInputChange(key, field, value)}
            />
          ))}
        </div>
      </div>
      <div className="buttons-container">
        <Button
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveData}
          disabled={disableButton || !diuoValue}
        >
          Enregistrer
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<PdfIcon />}
          onClick={handleGenClick}
          disabled={disableButton || !diuoValue}
        >
          Générer
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<VisibilityIcon />}
          onClick={() => handleShowPdf()}
          disabled={disableButton || !diuoValue}
        >
          Afficher
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={prevPage}
          disabled={disableButton || !diuoValue}
        >
          Précedent
        </Button>
        <Button
          color="primary"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={nextPage}
          disabled={disableButton || !diuoValue}
        >
          Suivant
        </Button>
        {disableButton && (
          <CircularProgress className="spinner" color="inherit" />
        )}
      </div>
      <GenPdf
        file={file}
        isOpen={isOpen}
        closeDoc={() => setIsOpen(false)}
        scale={1}
      />
    </div>
  );
};

export default DiuoPage7;